import React, { forwardRef, useEffect } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { Field, Form, Formik, ErrorMessage } from 'formik'
import * as yup from 'yup'
import Box from '@mui/system/Box'
import AdxInput from 'components/custom/AdxInput'
import Typography from '@mui/material/Typography'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxDatePicker from 'components/custom/AdxDatePicker'
import { postNewReport, putEditReport } from 'services/api/modules/claim/report'
import { useNavigate } from 'react-router-dom'
import { addDays, set } from 'date-fns'

const NewClaimReport = forwardRef((props, ref) => {
  const {
    setNewClaimReport,
    setButtonLoading,
    selectedCategory,
    reportData,
    handleEditReportClose,
  } = props

  const handleResetReport = (boolean) => {
    setNewClaimReport(!boolean)
  }

  const navigate = useNavigate()

  const validationSchema = yup.object({
    title: yup.string().required('Name is required'),
    description: yup.string().min(8, 'Too short').required('Purpose is required'),
    claim_report_type: yup.number().required('Type is required'),
    start_date: yup.date().required('Start date is required'),

    end_date: yup
      .date()
      .required('End date is required')
      .min(yup.ref('start_date'), "End date can't be before start date"),
  })

  // send post api for new report
  const queryClient = useQueryClient()
  const { isLoading, error, isSuccess, mutate } = useMutation(
    (reportValues) =>
      reportData ? putEditReport(reportValues, reportData.id) : postNewReport(reportValues),
    {
      onSuccess: async (res) => {
        if (reportData) {
          queryClient.invalidateQueries('singleReportData', { exact: true })
          handleEditReportClose()
        } else {
          await queryClient.invalidateQueries('allReportData')
          navigate(`/app/claim/reports/${res.data.report.id}`, {
            state: {
              fromActionButton: true,
              selectedCategory: selectedCategory,
            },
          })
        }
      },
      onError: async (e) => {
        if (e?.status === 422) {
          const { errors } = e.data
          Object.keys(errors).forEach((key) => {
            ref?.current?.setFieldError(key, errors[key])
          })
        }
        console.log(e)
      },
    }
  )

  useEffect(() => {
    setButtonLoading(isLoading)
  }, [isLoading])

  return (
    <Formik
      innerRef={ref}
      enableReinitialize
      initialValues={{
        title: reportData?.title ? reportData?.title : '',
        claim_report_type: reportData?.claimReportType ? reportData?.claimReportType : '',
        description: reportData?.description ? reportData?.description : '',
        start_date: reportData?.startDate ? reportData?.startDate : '',
        end_date: reportData?.endDate ? reportData?.endDate : '',
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => mutate(values)}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
        return (
          <Form>
            <Box display="flex" flexDirection="column" rowGap={{ xs: 1.2, lg: 1 }}>
              <Box
                width={1}
                display="flex"
                flexDirection={{ xs: 'column', lg: 'row' }}
                alignItems="start"
                justifyContent="space-between"
                columnGap={{ xs: 0, lg: 2 }}
                rowGap={{ xs: 1.2, lg: 0 }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  columnGap={1}
                  sx={{ width: { xs: 1, lg: 2 / 3 } }}
                >
                  <Field
                    name="title"
                    as={AdxInput}
                    charLimit={50}
                    sx={{ width: 1 }}
                    label="Claim report name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorMessage={<ErrorMessage name="title" component={AdxErrorMessage} />}
                    isErrorShown={errors.title && touched.title}
                  />
                  {/* {!reportData && (
                    <Typography
                      variant="body1"
                      color="alternate.main"
                      marginTop={1}
                      sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={() => handleResetReport(true)}
                    >
                      Reset
                    </Typography>
                  )} */}
                </Box>
                <Box height={1} sx={{ width: { xs: 1, lg: 1 / 3 } }}>
                  <Field
                    name="claim_report_type"
                    as={AdxDropdown}
                    label="Type"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorMessage={
                      <ErrorMessage name="claim_report_type" component={AdxErrorMessage} />
                    }
                    isErrorShown={errors.claim_report_type && touched.claim_report_type}
                  >
                    <AdxMenuItem value={1} selectedvalue={values.claim_report_type}>
                      Personal
                    </AdxMenuItem>
                    <AdxMenuItem value={2} selectedvalue={values.claim_report_type}>
                      Company
                    </AdxMenuItem>
                  </Field>
                </Box>
              </Box>
              <Box>
                <Field
                  name="description"
                  as={AdxInput}
                  multiline
                  minRows={2}
                  sxInput={{ padding: 2 }}
                  charLimit={120}
                  sx={{ width: 1 }}
                  label="Purpose"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={<ErrorMessage name="description" component={AdxErrorMessage} />}
                  isErrorShown={errors.description && touched.description}
                />
              </Box>
              <Box
                width={1}
                display="flex"
                flexDirection={{ xs: 'column', lg: 'row' }}
                alignItems="start"
                justifyContent="space-between"
                columnGap={{ xs: 0, lg: 2 }}
                rowGap={{ xs: 1.2, lg: 0 }}
              >
                <Field
                  name="start_date"
                  as={AdxDatePicker}
                  blockFuture={true}
                  sx={{ width: { xs: 1, lg: 1 / 2 } }}
                  label="Start date"
                  onChange={(e) => {
                    if (e === null) {
                      setFieldValue('start_date', '')
                    } else {
                      setFieldValue(
                        'start_date',
                        set(e, {
                          hours: 0,
                          minutes: 0,
                          seconds: 0,
                          milliseconds: 0,
                        })
                      )
                    }
                  }}
                  onBlur={handleBlur}
                  errorMessage={<ErrorMessage name="start_date" component={AdxErrorMessage} />}
                  isErrorShown={errors.start_date && touched.start_date}
                />
                <Field
                  name="end_date"
                  as={AdxDatePicker}
                  blockFuture={true}
                  sx={{ width: { xs: 1, lg: 1 / 2 } }}
                  label="End date"
                  onChange={(e) => {
                    if (e === null) {
                      setFieldValue('end_date', '')
                    } else {
                      setFieldValue(
                        'end_date',
                        set(e, {
                          hours: 0,
                          minutes: 0,
                          seconds: 0,
                          milliseconds: 0,
                        })
                      )
                    }
                  }}
                  minDate={addDays(values.start_date, 1)}
                  onBlur={handleBlur}
                  errorMessage={<ErrorMessage name="end_date" component={AdxErrorMessage} />}
                  isErrorShown={errors.end_date && touched.end_date}
                />
              </Box>
            </Box>
            <button type="submit" hidden>
              Submit
            </button>
          </Form>
        )
      }}
    </Formik>
  )
})

export default NewClaimReport
